/**
 * MaestroAreas
 * Página para gestionar el maestro de Áreas
 * CRUD completo para crear, editar, listar y eliminar áreas
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import presupuestoHierarchyService from '../../services/presupuestoHierarchyService';
import CrearAreaModal from '../../components/maestros/CrearAreaModal';
import EditarAreaModal from '../../components/maestros/EditarAreaModal';
import Loading from '../../components/Loading';
import '../../styles/maestros.css';

const MaestroAreas = () => {
  const navigate = useNavigate();
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [crearModalOpen, setCrearModalOpen] = useState(false);
  const [editarModalOpen, setEditarModalOpen] = useState(false);
  const [areaSeleccionada, setAreaSeleccionada] = useState(null);

  useEffect(() => {
    cargarAreas();
  }, []);

  const cargarAreas = async () => {
    setLoading(true);
    try {
      const response = await presupuestoHierarchyService.getMaestroAreas();
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setAreas(data);
    } catch (error) {
      console.error('Error al cargar áreas:', error);
      toast.error('Error al cargar las áreas');
    } finally {
      setLoading(false);
    }
  };

  const handleEliminarArea = async (areaId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta área?')) {
      return;
    }

    try {
      await presupuestoHierarchyService.deleteMaestroArea(areaId);
      toast.success('Área eliminada exitosamente');
      cargarAreas();
    } catch (error) {
      console.error('Error al eliminar área:', error);
      toast.error('Error al eliminar el área');
    }
  };

  const handleCrearArea = async (datosArea) => {
    try {
      await presupuestoHierarchyService.createMaestroArea(datosArea);
      toast.success('Área creada exitosamente');
      setCrearModalOpen(false);
      cargarAreas();
    } catch (error) {
      console.error('Error al crear área:', error);
      toast.error(error.response?.data?.detail || 'Error al crear el área');
    }
  };

  const handleEditarArea = async (datosArea) => {
    try {
      await presupuestoHierarchyService.updateMaestroArea(areaSeleccionada.id, datosArea);
      toast.success('Área actualizada exitosamente');
      setEditarModalOpen(false);
      setAreaSeleccionada(null);
      cargarAreas();
    } catch (error) {
      console.error('Error al actualizar área:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar el área');
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="maestro-areas">
      <div className="header">
        <div>
          <h1>Maestro de Áreas</h1>
          <p style={{ margin: '0.5rem 0 0 0', color: '#666', fontSize: '0.95rem' }}>
            Total de áreas: <strong>{areas.length}</strong>
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/maestros/partidas')}
            title="Gestionar partidas"
          >
            Gestionar Partidas
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setCrearModalOpen(true)}
          >
            <FiPlus /> Nueva Área
          </button>
        </div>
      </div>

      {areas.length === 0 ? (
        <div className="empty-state">
          <p>No hay áreas creadas aún</p>
          <button
            className="btn btn-primary"
            onClick={() => setCrearModalOpen(true)}
          >
            Crear Primera Área
          </button>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <thead>
              <tr style={{
                background: 'linear-gradient(135deg, #102826 0%, #69B32D 100%)',
                color: 'white'
              }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Nombre</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Descripción</th>
                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600' }}>Estado</th>
                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {areas.map((area, index) => (
                <tr key={area.id} style={{
                  borderBottom: '1px solid #e5e7eb',
                  transition: 'background-color 0.2s',
                  backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#f9fafb'}
                >
                  <td style={{ padding: '1rem', fontWeight: '500' }}>{area.nombre}</td>
                  <td style={{ padding: '1rem', color: '#6b7280', fontSize: '0.875rem' }}>
                    {area.descripcion || '-'}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '9999px',
                      backgroundColor: area.status === 'activo' ? '#dcfce7' : '#fee2e2',
                      color: area.status === 'activo' ? '#15803d' : '#991b1b',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      textTransform: 'capitalize'
                    }}>
                      {area.status}
                    </span>
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => {
                          setAreaSeleccionada(area);
                          setEditarModalOpen(true);
                        }}
                        title="Editar área"
                        style={{
                          padding: '0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleEliminarArea(area.id)}
                        title="Eliminar área"
                        style={{
                          padding: '0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CrearAreaModal
        isOpen={crearModalOpen}
        onClose={() => setCrearModalOpen(false)}
        onSubmit={handleCrearArea}
      />

      {areaSeleccionada && (
        <EditarAreaModal
          isOpen={editarModalOpen}
          onClose={() => {
            setEditarModalOpen(false);
            setAreaSeleccionada(null);
          }}
          area={areaSeleccionada}
          onSubmit={handleEditarArea}
        />
      )}
    </div>
  );
};

export default MaestroAreas;
